import React, { useMemo, useState } from 'react';
import { useParams, useHistory } from 'react-router';
import { FormattedMessage } from 'react-intl';

import { GET_BOOKING } from 'store/queries';
import { useManagedQuery } from 'utils/managedHooks';
import { Column } from 'components/shared/Layout';
import { Spinner } from 'admin-components/components';
import AddAssistanceForm from 'components/shared/AddAssistance/AddAssistanceForm';
import { ConfirmButton } from 'components/ui/Button';
import productTypes from 'constants/productTypes';
import { INBOUND, OUTBOUND } from 'constants/booking';
import { GET_EXTRA_PRODUCTS } from 'components/shared/AddAssistance/queries';
import { bookingStatus } from 'types/booking';

import { formatExtras, formatPassengers, formatSegments } from './ExchangeSpecialAssistance.utils';
import { AssistanceFormContainer } from './ExchangeSpecialAssistance.styles';

const ExchangeSpecialAssistance = () => {
  const [loading, setLoading] = useState(false);

  const history = useHistory();
  const { reference } = useParams();

  const newPassengersIds = useMemo(() => {
    if (history?.location?.search) {
      return new URLSearchParams(history?.location?.search).get('newPassengersIds')?.split(',') ?? [];
    }

    return [];
  }, [history?.location?.search]);

  const { data: bookingData, loading: bookingLoading } = useManagedQuery(GET_BOOKING, {
    variables: { reference },
    fetchPolicy: 'network-only'
  });

  const {
    pendingCreationLegs = [],
    outbound = [],
    inbound = [],
    currency,
    isGroupBooking,
    isInAfterSales,
    status,
    passengers = [],
    outboundExtras = [],
    inboundExtras = []
  } = bookingData?.booking ?? {};

  const { newSegments, newPassengers } = useMemo(() => {
    if (pendingCreationLegs?.length > 0) {
      return {
        newSegments: pendingCreationLegs.filter((segment) => !segment.cancelled),
        newPassengers: pendingCreationLegs.flatMap(({ products }) =>
          products.filter(({ provisional }) => provisional).map(({ passenger }) => passenger)
        )
      };
    } else if (newPassengersIds.length > 0) {
      return {
        newSegments: [...outbound, ...inbound].filter(
          (segment) => !segment.cancelled && segment.products.some(({ passengerId }) => newPassengersIds.includes(passengerId))
        ),
        newPassengers: passengers.filter(({ id }) => newPassengersIds.includes(id))
      };
    }

    return {
      newSegments: [],
      newPassengers: []
    };
  }, [inbound, newPassengersIds, outbound, passengers, pendingCreationLegs]);

  const { data: extrasData = {}, loading: extrasLoading } = useManagedQuery(GET_EXTRA_PRODUCTS, {
    skip: !newSegments.length || !newPassengers.length || !currency,
    variables: {
      currency: currency,
      segments: formatSegments(newSegments),
      passengers: formatPassengers(newPassengers),
      types: [productTypes.DISABILITY_SUPPORT],
      isAftersales: true
    }
  });

  if (bookingLoading || extrasLoading) {
    return (
      <Column alignItems="center" justifyContent="center">
        <Spinner />
      </Column>
    );
  }

  const handleNextRoute = () => {
    let nextRoute = '';

    const membershipNumber = new URLSearchParams(history?.location?.search).get('membershipNumber');
    const addPassengers = new URLSearchParams(history?.location?.search).get('addPassengers');

    const params = new URLSearchParams();

    params.append('currency', currency);

    if (membershipNumber) {
      params.append('membershipNumber', membershipNumber);
    }

    if (addPassengers) {
      params.append('addPassengers', addPassengers);
    }

    if (isGroupBooking && status === bookingStatus.TEMPORARILY_ON_HOLD) {
      nextRoute = `/manage/bookings/${reference}/journeys`;
    } else {
      nextRoute = `/exchange/${reference}/confirm?${params.toString()}`;
    }

    history.push(nextRoute);
  };

  const { specialAssistance = [] } = extrasData?.getExtraProducts ?? {};

  const isReturn =
    pendingCreationLegs.some(({ direction }) => direction === OUTBOUND) &&
    pendingCreationLegs.some(({ direction }) => direction === INBOUND);

  return (
    <AssistanceFormContainer>
      <AddAssistanceForm
        showAssistance
        isPackageBooking={false}
        assistanceOptions={formatExtras(outbound, inbound, specialAssistance)}
        extrasOptions={[]}
        outboundExtras={outboundExtras}
        inboundExtras={inboundExtras}
        bookingReference={reference}
        onNext={handleNextRoute}
        passengers={newPassengers}
        currency={currency}
        setLoading={setLoading}
        setIsConfirmDisabled={() => null}
        isReturn={isReturn}
        shouldConfirmBooking={!isInAfterSales}
      />
      <ConfirmButton
        variant="primary"
        data-cy="exchanges-special-assistance-continue"
        type="submit"
        form="add-assistance-form"
        loading={loading || bookingLoading || extrasLoading}
        disabled={loading || bookingLoading || extrasLoading}
      >
        <FormattedMessage id="exchanges.continue" />
      </ConfirmButton>
    </AssistanceFormContainer>
  );
};

export default ExchangeSpecialAssistance;
